import {
    collection,
    doc,
    setDoc,
    getDoc,
    getDocs,
    query,
    where,
    updateDoc,
    deleteDoc,
    serverTimestamp,
    Timestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
import { Invitation, Company, User } from './types';
import { companyService } from './companyService';
import { userService } from './userService';
import { v4 as uuidv4 } from 'uuid';

export class InvitationService {
    private collectionName = 'invitations';

    async createInvitation(
        inviterId: string,
        email: string,
        companyData: Omit<Company, 'id' | 'createdAt' | 'updatedAt' | 'settings' | 'status' | 'metadata'>,
        userProfile: Omit<User['profile'], 'avatar'>
    ): Promise<Invitation> {
        try {
            // 0a. Check duplicate: pending invitation for same email
            const existingInviteQ = query(
                collection(db, this.collectionName),
                where('email', '==', email),
                where('status', '==', 'pending')
            );
            const existingInviteSnap = await getDocs(existingInviteQ);
            if (!existingInviteSnap.empty) {
                throw new Error('companies_page.errors.invitation_already_sent');
            }

            // 0b. Check duplicate: company with same ICO already exists
            const existingIcoQ = query(
                collection(db, 'companies'),
                where('ico', '==', companyData.ico)
            );
            const existingIcoSnap = await getDocs(existingIcoQ);
            if (!existingIcoSnap.empty) {
                throw new Error('companies_page.errors.company_ico_exists');
            }

            // 1. Create Company
            const company: Omit<Company, 'id' | 'createdAt' | 'updatedAt'> = {
                ...companyData,
                status: 'pending_verification',
                email: email,
                settings: {
                    notifications: true,
                    emailMarketing: true,
                    language: 'sk',
                    timezone: 'Europe/Bratislava',
                },
                metadata: {
                    registrationSource: 'invitation',
                },
            };
            const createdCompany = await companyService.createCompany(company);

            // 2. Create User record in Firestore (without Auth UID yet)
            const userData: Omit<User, 'id' | 'createdAt' | 'updatedAt' | 'teamMemberId'> = {
                email: email,
                companyId: createdCompany.id,
                role: 'company_admin',
                status: 'active',
                profile: userProfile,
                permissions: ['manage_company', 'create_transports', 'view_orders'],
                emailVerified: false,
            };
            // We generate a temp ID for the user because we don't have Auth UID yet
            const tempUserId = doc(collection(db, 'users')).id;
            const createdUser = await userService.createUser(userData, tempUserId);

            // 3. Create Invitation. Doc id == token so the unauthenticated invitee
            // can fetch it with a single-doc get (rules: invitations get if true),
            // since the tenant `list` rule would deny a token query for a logged-out user.
            const token = uuidv4();
            const expiresAt = new Date();
            expiresAt.setDate(expiresAt.getDate() + 7); // 7 days expiry

            const invitation: Invitation = {
                id: token,
                email: email,
                companyId: createdCompany.id,
                userId: createdUser.id,
                token: token,
                invitedBy: inviterId,
                status: 'pending',
                expiresAt: Timestamp.fromDate(expiresAt),
                createdAt: serverTimestamp() as Timestamp,
                updatedAt: serverTimestamp() as Timestamp,
            };

            await setDoc(doc(db, this.collectionName, token), invitation);

            return invitation;
        } catch (error: any) {
            // Re-throw i18n key errors (duplicate checks) directly
            if (error.message?.startsWith('companies_page.errors.')) {
                throw error;
            }
            console.error('Error creating invitation:', error);
            throw new Error('companies_page.errors.submit_error');
        }
    }

    async getInvitationByToken(token: string): Promise<Invitation | null> {
        try {
            // Single-doc get (doc id == token) so an unauthenticated invitee can read it.
            const snap = await getDoc(doc(db, this.collectionName, token));
            if (!snap.exists()) return null;

            const invitation = snap.data() as Invitation;
            if (invitation.status !== 'pending') return null;

            // Check expiry (the 'expired' write may fail for a logged-out user — harmless)
            if (invitation.expiresAt.toDate() < new Date()) {
                await this.updateInvitationStatus(token, 'expired');
                return null;
            }

            return invitation;
        } catch (error) {
            console.error('Error getting invitation:', error);
            return null;
        }
    }

    async updateInvitationStatus(invitationId: string, status: Invitation['status']): Promise<void> {
        try {
            const ref = doc(db, this.collectionName, invitationId);
            await updateDoc(ref, {
                status,
                updatedAt: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error updating invitation status:', error);
        }
    }

    /**
     * Invite an employee into an EXISTING company. Creates only the invitation
     * doc (token + role + profile + 7-day expiry); the single user doc is created
     * at redeem keyed by the Auth UID, so there is no temp/ghost/orphan user doc.
     * The recipient gets a `/setup-password/:token` email and joins this company.
     */
    async createEmployeeInvitation(
        inviterId: string,
        companyId: string,
        employee: {
            email: string;
            firstName: string;
            lastName: string;
            position: string;
            phone: string;
            role: User['role'];
        }
    ): Promise<Invitation> {
        // Doc id == token → unauthenticated invitee can fetch via single-doc get.
        const token = uuidv4();
        const expiresAt = new Date();
        expiresAt.setDate(expiresAt.getDate() + 7); // 7 days expiry

        const invitation: Invitation = {
            id: token,
            email: employee.email,
            companyId,
            token,
            invitedBy: inviterId,
            status: 'pending',
            role: employee.role,
            firstName: employee.firstName,
            lastName: employee.lastName,
            position: employee.position,
            phone: employee.phone,
            expiresAt: Timestamp.fromDate(expiresAt),
            createdAt: serverTimestamp() as Timestamp,
            updatedAt: serverTimestamp() as Timestamp,
        };

        await setDoc(doc(db, this.collectionName, token), invitation);
        return invitation;
    }

    /**
     * Redeem an invitation: create the Firebase Auth account for the invited
     * email, write the single `users/{authUid}` doc, and mark the invitation
     * accepted. Employee invites build the user doc from the invitation payload
     * (joining the existing company); the legacy company-invite flow copies the
     * temp user doc referenced by `invitation.userId`. Returns the created user.
     */
    async redeemInvitation(token: string, password: string): Promise<User> {
        const invitation = await this.getInvitationByToken(token);
        if (!invitation) {
            throw new Error('auth.invitation.invalid_or_expired');
        }

        const { createUserWithEmailAndPassword } = await import('firebase/auth');
        const { auth } = await import('../../firebase');
        const credential = await createUserWithEmailAndPassword(auth, invitation.email, password);
        const uid = credential.user.uid;

        let userData: Omit<User, 'id' | 'createdAt' | 'updatedAt' | 'teamMemberId'>;
        if (invitation.userId) {
            // Legacy company-invite flow: copy the pre-created temp user doc.
            const temp = await userService.getUser(invitation.userId);
            if (!temp) {
                throw new Error('auth.invitation.invalid_or_expired');
            }
            userData = { ...temp, emailVerified: true };
        } else {
            // Employee invite: build the user doc from the invitation payload.
            // `invitationToken` is persisted so Firestore rules can verify this
            // self-create against the (open-get) invitation doc — it proves this
            // Auth email was invited to THIS company, blocking a forged foreign
            // companyId (cross-tenant breakout). See firestore.rules users/create.
            userData = {
                email: invitation.email,
                companyId: invitation.companyId,
                role: invitation.role || 'company_user',
                status: 'active',
                profile: {
                    firstName: invitation.firstName || '',
                    lastName: invitation.lastName || '',
                    position: invitation.position || '',
                    phone: invitation.phone || '',
                },
                permissions: ['read'],
                emailVerified: true,
                invitationToken: token,
            };
        }

        const created = await userService.createUser(userData, uid);

        // Legacy hygiene (T16): the pre-created temp users/{tempId} doc is now
        // superseded by the UID-keyed doc — delete it so no ghost user remains
        // (the AM CARGO case). Best-effort: a cleanup failure must not fail the
        // redeem, but is logged so it can be purged manually.
        if (invitation.userId && invitation.userId !== uid) {
            try {
                await deleteDoc(doc(db, 'users', invitation.userId));
            } catch (cleanupError) {
                console.error(
                    `Failed to delete temp invitation user doc users/${invitation.userId}:`,
                    cleanupError
                );
            }
        }

        await this.updateInvitationStatus(invitation.id, 'accepted');
        return created;
    }
}

export const invitationService = new InvitationService();
